// Developer-docs content (#33), kept separate from the components so it can be
// unit-tested without a DOM render. Every snippet targets the REAL surface:
// the `@developerz.ai/ggcoin-sdk` `GGCoinClient`, the `/mcp/tokens` agent mint, and the
// HMAC-signed webhook — so a reader can copy-paste and have it work.

/** Where the public API lives (placeholder host until DNS is finalised). */
export const API_BASE_URL = "https://api.ggcoin.ai";
/** Dashboard where an owner self-issues API keys. */
export const DASHBOARD_URL = "https://dashboard.ggcoin.ai";

export interface Concept {
  title: string;
  body: string;
}

/** Core mental model a developer needs before the quickstart. */
export const CONCEPTS: readonly Concept[] = [
  {
    title: "Closed-loop by design",
    body: "Fiat flows in and mints coin; coin never flows back out. There is no payout or redeem path — a deliberate legal + product constraint that removes chargeback exposure. All balances are integers in the smallest unit (no floats).",
  },
  {
    title: "Master + sub-wallets",
    body: "Each account has one master wallet and any number of sub-wallets. Sub-wallets carry their own spend caps and approval thresholds, so you can scope a game server, a service, or an agent without exposing the whole balance.",
  },
  {
    title: "API keys + scopes",
    body: "Server-to-server calls authenticate with a scoped API key (header `x-api-key`). Scopes are coarse and explicit: wallet:read, wallet:write, transaction:write, agent:provision. Keys carry an optional per-day request quota.",
  },
  {
    title: "The agent layer (MCP)",
    body: "Autonomous agents get sub-wallet-scoped tokens minted over `/mcp/tokens` and spend through an MCP server. Spends above a sub-wallet's threshold escalate to a master push-approval — the first money rail built for software that spends on its own.",
  },
  {
    title: "Signed webhooks",
    body: "Every outbound event is HMAC-signed (timestamp + nonce, replay-protected) in the `x-ggcoin-signature` header and retried with backoff. Verify the signature before trusting a delivery; the dashboard logs every attempt.",
  },
];

export interface QuickstartStep {
  title: string;
  body: string;
  /** Language tag for the snippet (for the code block label). */
  lang: string;
  code: string;
}

/** Copy-paste path from zero to a first posted transaction. */
export const QUICKSTART: readonly QuickstartStep[] = [
  {
    title: "1 · Install the SDK",
    body: "The JS/TS SDK wraps the HTTP API with typed methods.",
    lang: "bash",
    code: "bun add @developerz.ai/ggcoin-sdk   # or: npm i @developerz.ai/ggcoin-sdk",
  },
  {
    title: "2 · Issue an API key",
    body: "From the dashboard, create a key with the scopes you need (e.g. transaction:write) and an optional daily quota. The plaintext key is shown once — store it as a secret.",
    lang: "text",
    code: `${DASHBOARD_URL}  →  API keys  →  Issue a key`,
  },
  {
    title: "3 · Initialise the client",
    body: "Construct a client with your key; it sends `x-api-key` on every call.",
    lang: "ts",
    code: `import { GGCoinClient } from "@developerz.ai/ggcoin-sdk";

const gg = new GGCoinClient({
  baseUrl: "${API_BASE_URL}",
  apiKey: process.env.GGCOIN_KEY,
});`,
  },
  {
    title: "4 · Read a balance",
    body: "Balances come back as smallest-unit strings — format at the edge.",
    lang: "ts",
    code: `const { balance } = await gg.getBalance(walletId);
console.log(balance); // e.g. "125000" = 1250.00 GG`,
  },
  {
    title: "5 · Move coin (closed-loop transfer)",
    body: "Send from one of your sub-wallets to another account's master wallet. Pass an idempotency key so a retry never double-spends.",
    lang: "ts",
    code: `const res = await gg.transfer({
  sourceSubWalletId: player.subWalletId,
  recipientWalletId: shopWalletId,
  amount: "250",                // 2.50 GG, smallest units
  idempotencyKey: crypto.randomUUID(),
});
// res.kind: "posted" | "require-approval" | "reject"`,
  },
  {
    title: "6 · Receive webhooks",
    body: "Register an HTTPS endpoint, then verify the signature on each delivery before acting on it. The header is `t=<ms>,n=<nonce>,v1=<hmac>`; recompute HMAC-SHA256 over `t.n.rawBody` with your endpoint secret.",
    lang: "ts",
    code: `import { createHmac, timingSafeEqual } from "node:crypto";

function verify(rawBody: string, header: string, secret: string) {
  const p = Object.fromEntries(header.split(",").map((kv) => kv.split("=")));
  const expected = createHmac("sha256", secret)
    .update(\`\${p.t}.\${p.n}.\${rawBody}\`)
    .digest("hex");
  return timingSafeEqual(Buffer.from(expected), Buffer.from(p.v1));
}
// if (!verify(rawBody, req.headers["x-ggcoin-signature"], secret)) return 401;`,
  },
];

export interface AgentStep {
  title: string;
  body: string;
  lang?: string;
  code?: string;
}

/** The MCP / agent-economy path — the product's differentiator. */
export const AGENT_GUIDE: readonly AgentStep[] = [
  {
    title: "Mint a sub-wallet-scoped agent token",
    body: "A server holding a key with the agent:provision scope mints an agent token bound to one sub-wallet — no user login required. A leaked token only ever exposes that sub-wallet.",
    lang: "bash",
    code: `curl -X POST ${API_BASE_URL}/mcp/tokens \\
  -H "x-api-key: $GGCOIN_KEY" \\
  -H "content-type: application/json" \\
  -d '{"subWalletId":"<sub-wallet-id>"}'
# → { "token": "...", "subWalletId": "...", "expiresAt": "..." }`,
  },
  {
    title: "Connect the agent over MCP",
    body: "The agent opens the MCP channel with its token and gets exactly two tools: get_balance and authorize_spend. Hard spend caps are enforced server-side; nothing the agent sends can exceed them.",
    lang: "text",
    code: `GET ${API_BASE_URL}/mcp?token=<agent-token>   (SSE)`,
  },
  {
    title: "Above-threshold spends escalate",
    body: "When an agent's spend exceeds its sub-wallet approval threshold, the call returns require-approval and a push lands in the master's mobile approval inbox — a 3DS-style human-in-the-loop for autonomous money.",
  },
];

export interface ApiEndpoint {
  method: string;
  path: string;
  summary: string;
}

/** A compact reference of the most-used endpoints (full set in the SDK types). */
export const API_REFERENCE: readonly ApiEndpoint[] = [
  { method: "GET", path: "/wallets/:id/balance", summary: "Read a wallet or sub-wallet balance" },
  { method: "POST", path: "/wallets/sub-wallets", summary: "Create a spend-capped sub-wallet" },
  {
    method: "POST",
    path: "/transactions/transfer",
    summary: "Closed-loop transfer between accounts",
  },
  { method: "POST", path: "/transactions/buy", summary: "Mint coin against a confirmed payment" },
  { method: "POST", path: "/mcp/tokens", summary: "Mint a sub-wallet-scoped agent token" },
  { method: "POST", path: "/webhooks/endpoints", summary: "Register a signed webhook endpoint" },
  { method: "POST", path: "/api-keys", summary: "Issue a scoped API key (+ optional quota)" },
];

/** Public SDK languages, JS first (the rest publish independently — see #34). */
export const SDKS: readonly string[] = ["JavaScript", "Lua", "Java", "Python", "C#", "PHP", "Go"];

/**
 * The in-page sections in document order (#273) — one source of truth for the
 * top nav, the desktop TOC rail, and the scrollspy, so a section can't be
 * linked without existing (or exist unlinked).
 */
export const TOC = [
  { id: "concepts", label: "Concepts" },
  { id: "quickstart", label: "Quickstart" },
  { id: "agents", label: "Agents" },
  { id: "api", label: "API" },
  { id: "sdks", label: "SDKs" },
] as const;

/** Slug for a heading anchor: lowercase, alphanumerics dash-joined. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
